import { useCallback, useEffect, useState } from "react";

import { formatInt, formatUsd } from "@kontextwerk/web-common";

import FilterBar from "../components/FilterBar";
import PendingCostNotice from "../components/PendingCostNotice";
import TopNBarChart from "../components/TopNBarChart";
import { fetchCostSummary } from "../lib/api";
import { defaultRange, type DateRange } from "../lib/dateRange";
import type { CostFilters, CostSummaryResponse } from "../lib/types";

export default function OrganizationsRoute() {
  const [range, setRange] = useState<DateRange>(defaultRange(30));
  const [filters, setFilters] = useState<CostFilters>({});
  const [summary, setSummary] = useState<CostSummaryResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await fetchCostSummary(
        range.from,
        range.to,
        "organization",
        filters,
      );
      setSummary(resp);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [range.from, range.to, filters]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="p-6 space-y-4">
      <header className="flex items-center justify-between">
        <h1 className="text-2xl font-medium">Kosten pro Organization</h1>
        <span className="text-xs text-kw-text-faint">
          {range.from} → {range.to}
        </span>
      </header>
      <p className="text-xs text-kw-text-faint max-w-3xl">
        kw:org-admin sieht hier nur die eigene Org (server-seitig gefiltert).
        kw:platform-admin sieht alle. Solo-Mode-Calls werden unter
        <span className="font-mono"> default </span>aggregiert.
      </p>
      <FilterBar
        range={range}
        onRangeChange={setRange}
        filters={filters}
        onFiltersChange={setFilters}
        showLayerFilter
      />
      {error ? (
        <div className="p-3 bg-red-900/30 border border-red-700/50 rounded text-sm text-red-300">
          {error}
        </div>
      ) : null}

      <PendingCostNotice pending={summary?.pending} />

      <section className="bg-kw-bg-elevated border border-kw-border-subtle rounded p-4">
        <h2 className="text-sm font-medium text-kw-text-muted mb-3">
          Org-Verteilung
        </h2>
        {loading && !summary ? (
          <p className="text-sm text-kw-text-faint">Lade…</p>
        ) : (
          <TopNBarChart buckets={summary?.buckets ?? []} topN={15} />
        )}
      </section>

      <section className="bg-kw-bg-elevated border border-kw-border-subtle rounded overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-kw-bg-overlay text-kw-text-muted text-xs">
            <tr>
              <th className="px-3 py-2 text-left font-medium">Organization</th>
              <th className="px-3 py-2 text-right font-medium">USD</th>
              <th className="px-3 py-2 text-right font-medium">Calls</th>
            </tr>
          </thead>
          <tbody>
            {(summary?.buckets ?? []).map((b) => (
              <tr key={b.group_key} className="border-t border-kw-border-subtle">
                <td className="px-3 py-2 font-mono">{b.group_key}</td>
                <td className="px-3 py-2 text-right font-mono">
                  {formatUsd(b.total_cost_usd)}
                </td>
                <td className="px-3 py-2 text-right">
                  {formatInt(b.total_calls)}
                </td>
              </tr>
            ))}
            {(summary?.buckets ?? []).length === 0 && !loading ? (
              <tr>
                <td
                  colSpan={3}
                  className="px-3 py-6 text-center text-kw-text-faint"
                >
                  Keine Org-Kosten im gewählten Zeitraum.
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </section>
    </div>
  );
}
